import { resolveBrandPalette } from '@supply-mover-2-0/shared';
import { useTheme } from '@supply-mover-2-0/web-ui';
import { useEffect, useMemo } from 'react';
import {
	capacitorStatusBarStyleFor,
	statusBarTextFor,
} from '../lib/statusBarStyle';
import { usePortalSiteStore } from '../stores/portalSiteStore';
import {
	selectStatusBarSurface,
	useStatusBarSurfaceStore,
} from '../stores/statusBarSurfaceStore';

/**
 * Applies the active portal_site's brand to the document (PD-16045).
 *
 * The brand supplies a primary and an accent (light and dark values); the
 * rest is fixed. `resolveBrandPalette` (libs/shared) turns those into every
 * value the storefront paints, per mode:
 *
 *   - `--brand-primary` is the header and bottom-nav fill: the primary in
 *     light mode, the neutral navy in dark mode. Its readable text rides on
 *     `--brand-primary-fg-rgb` (`text-brand-primary-fg` utilities).
 *   - `--brand-login-bg` is the pre-login welcome and login backdrop: the
 *     brand primary in both modes, so the identity stays put before sign-in.
 *   - `--color-portal-accent` is the button fill, `--color-portal-accent-fg`
 *     its label, `--color-portal-accent-text` the accent as link text on the
 *     card surface (index.css re-points `text-portal-accent` at it), and
 *     `--color-portal-cart` follows the accent as before.
 *   - `--brand-page-bg`, `--brand-page-bg-soft`, `--brand-surface`,
 *     `--brand-border`, `--brand-input` and `--brand-text-muted` are the fixed
 *     Workd neutrals for the mode. The brand never tints a background.
 *   - `faviconUrl` still swaps the <link rel="icon"> and Apple touch icon;
 *     `theme-color` follows the header.
 *   - In the native shell, the status bar's text color follows the surface
 *     the current page paints under it (`statusBarSurfaceStore`): the header
 *     band by default, the neutral page on pages without the band. This is
 *     the ONLY caller of the status-bar plugin's `setStyle`; pages declare
 *     their surface with `useStatusBarSurface` instead of calling it.
 *
 * Renders nothing. A side-effect component so it can sit inside <TenantGate>
 * and react when the bootstrap lands or the theme toggles.
 */
const FAVICON_REL_VALUES = ['icon', 'shortcut icon'];
const APPLE_TOUCH_REL = 'apple-touch-icon';
const META_THEME_COLOR = 'theme-color';

function setOrCreateMeta(name: string, value: string): void {
	let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
	if (el == null) {
		el = document.createElement('meta');
		el.setAttribute('name', name);
		document.head.appendChild(el);
	}
	el.setAttribute('content', value);
}

function setOrCreateLink(rel: string, href: string): void {
	let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
	if (el == null) {
		el = document.createElement('link');
		el.setAttribute('rel', rel);
		document.head.appendChild(el);
	}
	el.setAttribute('href', href);
}

function isNativeShell(): boolean {
	return (
		(
			window as { Capacitor?: { isNativePlatform?: () => boolean } }
		).Capacitor?.isNativePlatform?.() === true
	);
}

export function BrandThemeApplier(): null {
	const primaryColor = usePortalSiteStore((s) => s.site?.primaryColor ?? null);
	const accentColorLight = usePortalSiteStore(
		(s) => s.site?.accentColorLight ?? null,
	);
	const accentColorDark = usePortalSiteStore(
		(s) => s.site?.accentColor ?? null,
	);
	const faviconUrl = usePortalSiteStore((s) => s.site?.faviconUrl ?? null);

	// `isDark` flips when the user toggles in UserMenu; the palette below
	// re-resolves and the effects swap the CSS variables (and the status-bar
	// style) in place.
	const { isDark } = useTheme();
	const statusBarSurface = useStatusBarSurfaceStore(selectStatusBarSurface);

	const palette = useMemo(
		() =>
			resolveBrandPalette(
				{ primaryColor, accentColorLight, accentColorDark },
				isDark ? 'dark' : 'light',
			),
		[primaryColor, accentColorLight, accentColorDark, isDark],
	);

	useEffect(() => {
		const root = document.documentElement;
		const set = (name: string, value: string): void => {
			root.style.setProperty(name, value);
		};

		set('--brand-primary', palette.header);
		set('--brand-primary-fg-rgb', palette.headerTextRgb);
		setOrCreateMeta(META_THEME_COLOR, palette.themeColor);

		set('--brand-login-bg', palette.loginBackground);

		set('--brand-accent', palette.accent);
		set('--color-portal-accent', palette.accent);
		set('--color-portal-accent-hover', palette.accentHover);
		set('--color-portal-accent-fg', palette.accentText);
		set('--color-portal-accent-text', palette.accentLinkText);
		set('--color-portal-cart', palette.accent);
		set('--color-portal-cart-hover', palette.accentHover);

		const n = palette.neutrals;
		set('--brand-page-bg', n.page);
		set('--brand-page-bg-soft', n.pageSoft);
		set('--brand-surface', n.surface);
		set('--brand-border', n.border);
		set('--brand-input', n.input);
		set('--brand-text-muted', n.textMuted);
	}, [palette]);

	// Native shell (PD-16079): the system status bar overlays the top of the
	// page, so its text follows what is painted there rather than the theme.
	// On PortalLayout pages that is the header band: a brand whose primary is
	// dark (Capital Trading's teal) needs white status text in light mode as
	// well, and a pale primary needs dark text. Pages without the band claim
	// the `page` surface, so a light page gets dark text even under a dark
	// brand (PD-16744). Capacitor names the style after the background it
	// suits (`Style.Dark` = light text); `capacitorStatusBarStyleFor` owns
	// that inversion.
	useEffect(() => {
		const text = statusBarTextFor(statusBarSurface, palette);
		// Mirrored onto <html> on every platform so the decision can be
		// checked in a plain browser (Playwright, devtools) without a device;
		// the plugin call below only runs inside the native shell.
		const root = document.documentElement;
		root.dataset.statusBarSurface = statusBarSurface;
		root.dataset.statusBarText = text;
		if (!isNativeShell()) {
			return;
		}
		void import('@capacitor/status-bar').then((statusBar) => {
			void statusBar.StatusBar.setStyle({
				style: statusBar.Style[capacitorStatusBarStyleFor(text)],
			}).catch(() => {});
		});
	}, [palette, statusBarSurface]);

	useEffect(() => {
		if (faviconUrl == null || faviconUrl.length === 0) {
			return;
		}
		// `shortcut icon` is the legacy attr — some Workd-default tooling
		// emits both, so we rewrite both rather than leave a stale one.
		for (const rel of FAVICON_REL_VALUES) {
			setOrCreateLink(rel, faviconUrl);
		}
		setOrCreateLink(APPLE_TOUCH_REL, faviconUrl);
	}, [faviconUrl]);

	return null;
}
